/**
 * Beacon home — Muse-like chat shell for the personal AWS agent.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Send, Sparkles, Network, Boxes, ListTodo, Lightbulb, ClipboardCheck } from 'lucide-react'
import api from '../utils/api'
import { Button } from '@/components/Button'
import { BeaconMarkdown } from '@/components/BeaconMarkdown'
import { BeaconWordmark } from '@/components/BeaconBrand'
import { useToast } from '@/components/Toast'
import { apiErrorMessage, parseApiError } from '@/utils/apiErrors'

interface ChatMessage {
  id?: string
  role: 'user' | 'assistant' | 'system' | 'tool'
  content: string
  tool_calls?: unknown
  created_at?: string
}

const QUICK = [
  { label: "What's in my account?", text: "what's in my account?" },
  { label: 'Show resources (week)', text: 'what did you create this week?' },
  { label: 'Refresh VPC', text: 'refresh vpc' },
  { label: 'Daily open-SG check', text: 'schedule daily open-sg check' },
  { label: 'Monitor account', text: 'monitor' },
]

export default function BeaconChat() {
  const { toast } = useToast()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [dryRun, setDryRun] = useState(true)
  const endRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/beacon/chat/messages', { params: { limit: 100 } })
      setMessages(res.data.messages || [])
    } catch {
      // first visit / empty
    }
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const send = async (text?: string) => {
    const message = (text ?? input).trim()
    if (!message || sending) return
    setInput('')
    setSending(true)
    setMessages((prev) => [...prev, { role: 'user', content: message }])
    try {
      const res = await api.post('/api/v1/beacon/chat', { message, dry_run: dryRun })
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: res.data.reply, tool_calls: res.data.tool_calls },
      ])
      // Soft CTA when chat returns a setup-guidance reply without throwing
      if (res.data?.code === 'AWS_ACCOUNT_NOT_LINKED' && res.data?.cta?.path) {
        toast.info('Finish setup', res.data.cta.label || 'Link an AWS account in Company settings')
      }
    } catch (err: unknown) {
      const parsed = parseApiError(err)
      const status = (err as { response?: { status?: number } })?.response?.status
      // Keep the thread readable — surface setup gaps as an assistant turn
      if (parsed.isSetupIncomplete) {
        const ctaNote = parsed.cta
          ? `\n\n→ [${parsed.cta.label}](${parsed.cta.path})`
          : '\n\n→ [Open Company Admin](/beacon/admin)'
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: `${parsed.message}${ctaNote}` },
        ])
      } else if (status === 402 || status === 429) {
        const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
        let msg = apiErrorMessage(err)
        if (detail && typeof detail === 'object') {
          const d = detail as { message?: string; upgrade_url?: string }
          msg = d.message || msg
          if (d.upgrade_url) msg = `${msg} Upgrade via AWS Marketplace.`
        }
        toast.error('Plan limit reached', msg)
      } else {
        toast.error('Beacon chat failed', apiErrorMessage(err))
      }
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] min-h-[520px] flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold tracking-[0.12em]"><BeaconWordmark className="font-semibold" textClassName="text-beacon-navy" /></p>
          <h2 className="font-display text-3xl text-slate-900">Your AWS engineering partner</h2>
          <p className="mt-1 max-w-xl text-sm text-slate-600">
            Ask Beacon against your company&apos;s linked AWS account. Discover, harden safely with
            dry-run + approvals, and remember what you created. Owners link AWS once under{' '}
            <Link className="font-medium text-teal-700 underline underline-offset-2 hover:text-teal-800" to="/beacon/admin">
              Company Admin
            </Link>
            — Team Members do not enter credentials in Settings.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <Link className="rounded-md border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-50" to="/beacon/resources"><Boxes className="mr-1 inline h-3.5 w-3.5" />Resources</Link>
          <Link className="rounded-md border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-50" to="/beacon/tasks"><ListTodo className="mr-1 inline h-3.5 w-3.5" />Tasks</Link>
          <Link className="rounded-md border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-50" to="/beacon/vpc"><Network className="mr-1 inline h-3.5 w-3.5" />VPC</Link>
          <Link className="rounded-md border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-50" to="/beacon/recommendations"><Lightbulb className="mr-1 inline h-3.5 w-3.5" />Alerts</Link>
          <Link className="rounded-md border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-50" to="/beacon/approvals"><ClipboardCheck className="mr-1 inline h-3.5 w-3.5" />Approvals</Link>
        </div>
      </div>

      <div className="relative flex flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-beacon-light via-white to-white shadow-sm">
        <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, #232F3E 0, transparent 40%), radial-gradient(circle at 80% 0%, #FF9900 0, transparent 35%)',
        }} />
        <div className="relative flex-1 space-y-5 overflow-y-auto px-4 py-4 sm:px-6">
          {messages.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-slate-500">
              <Sparkles className="h-8 w-8 text-beacon-orange" />
              <p className="font-display text-lg text-beacon-navy">Ask Beacon about your account</p>
              <p className="max-w-md text-sm">
                Start with “what&apos;s in my account?” then harden or create with dry-run on.
                High-risk changes land in Approvals. If AWS isn&apos;t linked yet, an Owner/Admin
                finishes that under{' '}
                <Link className="font-medium text-teal-700 underline underline-offset-2 hover:text-teal-800" to="/beacon/admin">
                  Company Admin
                </Link>
                — not Settings.
              </p>
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                {QUICK.map((q) => (
                  <button
                    key={q.label}
                    type="button"
                    onClick={() => send(q.text)}
                    className="rounded-md border border-beacon-orange/40 bg-white/80 px-3 py-1.5 text-xs text-beacon-navy hover:bg-beacon-orange/10"
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => {
            const next = messages[i + 1]
            // Clear request→response boundary without heavy dashboard chrome
            const showReplyDivider =
              m.role === 'user' && next != null && next.role !== 'user'

            if (m.role === 'user') {
              // Soft, content-sized bubble — not a full-bleed dark banner
              return (
                <div key={m.id || i} className="space-y-4">
                  <div className="flex justify-end">
                    <div className="flex w-fit max-w-[min(85%,28rem)] flex-col items-end gap-1">
                      <span className="px-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                        You
                      </span>
                      <div className="rounded-2xl rounded-br-md border border-beacon-navy/10 bg-beacon-navy/[0.07] px-3.5 py-2.5 text-sm leading-relaxed text-beacon-navy">
                        <p className="whitespace-pre-wrap break-words">{m.content}</p>
                      </div>
                    </div>
                  </div>
                  {showReplyDivider && (
                    <div
                      className="beacon-turn-divider"
                      role="separator"
                      aria-label="Assistant reply"
                    >
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full bg-beacon-orange/80"
                        aria-hidden
                      />
                    </div>
                  )}
                </div>
              )
            }
            // Assistant / system / tool — prose column with markdown
            return (
              <div key={m.id || i} className="flex justify-start">
                <div className="w-full max-w-[42rem]">
                  <span className="mb-1.5 block px-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-beacon-orange">
                    Beacon
                  </span>
                  <BeaconMarkdown>{m.content}</BeaconMarkdown>
                </div>
              </div>
            )
          })}
          <div ref={endRef} />
        </div>

        <div className="relative border-t border-slate-200/80 bg-white/80 px-4 py-3 backdrop-blur">
          <div className="mb-2 flex items-center justify-between gap-2 text-xs text-slate-500">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
              Dry-run AWS creates (recommended locally)
            </label>
            {sending && <span>Beacon is working…</span>}
          </div>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              send()
            }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Beacon — e.g. what's in my account?"
              className="flex-1 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm outline-none ring-beacon-orange/40 focus:ring-2"
            />
            <Button
              type="submit"
              disabled={sending || !input.trim()}
              className="!bg-beacon-navy hover:!bg-beacon-navy/90 focus:!ring-beacon-orange"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}
